import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";
import { useSelector } from "react-redux";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import UserAvatar from "../../../components/UserAvatar";
import PaginationComponent from "../../../components/Pagination";
import VerifiedBadge from "../../../components/VerifiedBadge";
import Badge from "../../../components/Badge";
import useUploadFile from "../../../hook/useUploadFile";
import ErrorMessage from "../../../components/ErrorMessage";
import AvatarCropper from "../../../components/AvatarCropper";
import OperatorIcon from "../../../components/OperatorIcon";
import DataExporter from "../../../components/DataExporter";
import { userAdminContext } from "../../../context/AdminContext/UserAdminContext/UserAdminContext";
import {
  changeUserPasswordValidation,
  createUserForAdminValidation,
  editUserForAdminValidation,
} from "../../../utils/validation/validation";
import { convertDate } from "../../../utils";
import {
  convertRoleToPath,
  convertRoleToText,
  exporterData,
  userRoleItems,
} from "../../../utils/enum";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";

const UsersAdmin = () => {
  const navigate = useNavigate();

  const auth = useSelector((state) => state.auth);

  const { handleUploadFile } = useUploadFile();

  const {
    users,
    user,
    setUser,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    isConfirm,
    setIsConfirm,
    role,
    setRole,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleCreateUser,
    handleEditUser,
    handleChangeUserConfirm,
    handleDeleteUser,
    handleVerifyUserPhoneNumber,
    handleVerifyUserEmail,
  } = useContext(userAdminContext);

  useEffect(() => {
    handleGetUsers();
  }, [start, end, pageNumber, role, itemsCount, sort, isConfirm]);

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      isEmpty(user) ? createUserForAdminValidation : editUserForAdminValidation,
    ),
  });

  return (
    <>
      <SiteTitle title="کاربران" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setIsActive={setIsConfirm}
        isActive={isConfirm}
        role={role}
        setRole={setRole}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <div className="d-flex justify-content-between align-items-center gap-2">
              {!isEmpty(users) && (
                <DataExporter
                  headers={exporterData.users.headers}
                  keys={exporterData.users.keys}
                  data={users}
                  fileName={exporterData.users.fileName}
                />
              )}
              <button
                className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                onClick={() => {
                  handleShowModal();
                  reset({ role: "CUSTOMER" });
                }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                افزودن کاربر
              </button>
            </div>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetUsers}
        />
        <div className="card-body p-24">
          {isEmpty(users) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">کاربر</th>
                      <th scope="col">شماره همراه</th>
                      <th scope="col">کد ملی</th>
                      <th scope="col">نقش</th>
                      <th scope="col">تاریخ ثبت نام</th>
                      <th scope="col">وضعیت</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <UserAvatar
                              className="ms-2"
                              {...(item?.avatar
                                ? { image: item?.avatar }
                                : {
                                    icon: "solar:user-broken",
                                  })}
                            />
                            <div className="flex-grow-1">
                              <span className="text-md mb-0 fw-normal text-secondary-light">
                                {item.firstName} {item.lastName}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {item.phoneNumber}
                            <VerifiedBadge
                              isVerified={item?.phoneNumberVerified}
                            />
                          </span>
                        </td>
                        <td>{item.nationalNumber}</td>
                        <td>{convertRoleToText(item.role)}</td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <Badge color={item.isConfirm ? "success" : "danger"}>
                            {item.isConfirm ? "فعال" : "غیرفعال"}
                          </Badge>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() =>
                                  navigate(
                                    `/${convertRoleToPath(auth.role)}/orders/${
                                      item._id
                                    }/${convertRoleToPath(item.role)}`,
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                گزارش سفارشات {convertRoleToText(item.role)}
                              </li>
                              <li
                                onClick={() => {
                                  reset();
                                  handleShowModal(item, setValue);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                ویرایش
                              </li>
                              <li
                                onClick={() =>
                                  setUser({ ...item, isPassword: true })
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                تغییر کلمه عبور
                              </li>

                              <li className="dropdown-divider" />

                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleVerifyUserPhoneNumber(item._id),
                                    `آیا از ${
                                      !item.phoneNumberVerified
                                        ? "تایید"
                                        : "عدم تایید"
                                    } کردن شماره موبایل کاربر ${
                                      item.firstName
                                    } ${item.lastName} مطمئنید؟`,
                                  )
                                }
                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-${
                                  !item.phoneNumberVerified
                                    ? "success"
                                    : "danger"
                                }`}
                              >
                                {!item.phoneNumberVerified
                                  ? "تایید"
                                  : "عدم تایید"}{" "}
                                شماره موبایل
                              </li>
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleVerifyUserEmail(item._id),
                                    `آیا از ${
                                      !item.emailVerified
                                        ? "تایید"
                                        : "عدم تایید"
                                    } کردن پست الکترونیک کاربر ${
                                      item.firstName
                                    } ${item.lastName} مطمئنید؟`,
                                  )
                                }
                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-${
                                  !item.emailVerified ? "success" : "danger"
                                }`}
                              >
                                {!item.emailVerified ? "تایید" : "عدم تایید"}{" "}
                                پست الکترونیک
                              </li>
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleChangeUserConfirm(item._id),
                                    `آیا از ${
                                      !item.isConfirm ? "فعال" : "غیرفعال"
                                    } کردن کاربر ${item.firstName} ${
                                      item.lastName
                                    } مطمئنید؟`,
                                  )
                                }
                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-${
                                  !item.isConfirm ? "success" : "danger"
                                }`}
                              >
                                {!item.isConfirm ? "فعال" : "غیرفعال"}
                              </li>
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteUser(item._id),
                                    `آیا از حذف کاربر ${item.firstName} ${item.lastName} مطمئنید؟`,
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(user) ? "افزودن کاربر جدید" : "ویرایش کاربر"}
          </span>
        </ModalHeader>
        <ModalBody>
          <div className="mb-40">
            <AvatarCropper
              onRemove={() => setValue("avatar", "")}
              value={watch("avatar")}
              onChange={(file) =>
                handleUploadFile(file, (e) => setValue("avatar", e), 1)
              }
            />
          </div>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(user) ? handleCreateUser : handleEditUser,
            )}
          >
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نام
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="firstName"
                      placeholder="نام را وارد نمایید"
                      {...register("firstName")}
                    />
                  </div>

                  <ErrorMessage message={errors.firstName?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="lastName"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نام خانوادگی
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="lastName"
                      placeholder="نام خانوادگی را وارد نمایید"
                      {...register("lastName")}
                    />
                  </div>

                  <ErrorMessage message={errors.lastName?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      شماره موبایل{" "}
                      <VerifiedBadge isVerified={user?.phoneNumberVerified} />
                    </label>

                    <div className="position-relative ">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control radius-8"
                        id="phoneNumber"
                        placeholder="شماره موبایل را وارد نمایید"
                        {...register("phoneNumber")}
                      />
                      <OperatorIcon phoneNumber={watch("phoneNumber")} />
                    </div>
                  </div>

                  <ErrorMessage message={errors.phoneNumber?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="nationalNumber"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      کدملی
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control radius-8"
                      id="nationalNumber"
                      placeholder="کدملی را وارد نمایید"
                      {...register("nationalNumber")}
                    />
                  </div>

                  <ErrorMessage message={errors.nationalNumber?.message} />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="role"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نقش
                    </label>
                    <select
                      className="form-control radius-8"
                      {...register("role")}
                    >
                      <option value="" selected hidden>
                        نقش را انتخاب نمایید
                      </option>
                      {userRoleItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("role") === item}
                        >
                          {convertRoleToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.role?.message} />
                </div>
              </div>
              {isEmpty(user) && (
                <>
                  <div className="col-sm-6">
                    <div className="mb-20">
                      <div>
                        <label
                          htmlFor="password"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          کلمه عبور
                        </label>
                        <input
                          type="password"
                          className="form-control radius-8"
                          id="password"
                          placeholder="کلمه عبور را وارد نمایید"
                          {...register("password")}
                        />
                      </div>

                      <ErrorMessage message={errors.password?.message} />
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="mb-20">
                      <div>
                        <label
                          htmlFor="confirmPassword"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          تکرار کلمه عبور
                        </label>
                        <input
                          type="password"
                          className="form-control radius-8"
                          id="confirmPassword"
                          placeholder="تکرار کلمه عبور را وارد نمایید"
                          {...register("confirmPassword")}
                        />
                      </div>

                      <ErrorMessage message={errors.confirmPassword?.message} />
                    </div>
                  </div>
                </>
              )}
              <div className="col-sm-12">
                <div className="mb-20">
                  <label
                    htmlFor="address"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    آدرس
                  </label>
                  <textarea
                    name="address"
                    className="form-control radius-8"
                    id="address"
                    rows={5}
                    placeholder="آدرس محل سکونت ..."
                    {...register("address")}
                  />
                </div>
              </div>
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(user) ? "افزودن" : "ویرایش"} کاربر
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ChangePassword />
    </>
  );
};

export default UsersAdmin;

const ChangePassword = () => {
  const { user, setUser, handleChangeUserPassword } =
    useContext(userAdminContext);

  // Validation
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changeUserPasswordValidation),
  });

  useEffect(() => {
    if (user.isPassword) reset();
  }, [user]);

  return (
    <Modal
      show={user.isPassword}
      className="modal-dialog-centered"
      size="md"
      onHide={() => setUser({})}
    >
      <ModalHeader closeButton>
        <span className="fs-5 fw-bold">
          ویرایش کلمه عبور {user?.firstName} {user?.lastName}
        </span>
      </ModalHeader>
      <ModalBody>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(handleChangeUserPassword)}
        >
          <div className="mb-10">
            <div>
              <label
                htmlFor="password"
                className="form-label fw-semibold text-primary-light text-sm mb-8"
              >
                کلمه عبور{" "}
                <span className="badge text-primary-600 bg-primary-100 radius-4">
                  جدید
                </span>
              </label>
              <input
                type="password"
                className="form-control radius-8"
                id="password"
                placeholder="کلمه عبور خود را وارد نمایید"
                {...register("password")}
              />
            </div>

            <ErrorMessage message={errors.password?.message} />
          </div>
          <div className="mb-10">
            <div>
              <label
                htmlFor="confirmPassword"
                className="form-label fw-semibold text-primary-light text-sm mb-8"
              >
                تکرار کلمه عبور{" "}
                <span className="badge text-primary-600 bg-primary-100 radius-4">
                  جدید
                </span>
              </label>
              <input
                type="password"
                className="form-control radius-8"
                id="confirmPassword"
                placeholder="تکرار کلمه عبور خود را وارد نمایید"
                {...register("confirmPassword")}
              />
            </div>

            <ErrorMessage message={errors.confirmPassword?.message} />
          </div>
          <div className="text-end mt-3">
            <button
              type="submit"
              className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
            >
              ویرایش کلمه عبور
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};
